import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth-provider';
import { DiscordActivityPlatformApi, Platform, usePlatform } from '@tofu/platform';
import { useDiscordId } from '../hooks/use-discord-id';

type DiscordEventsApi = {
  isDiscordUserSpeaking: (discordUserId?: string | null) => boolean;
};

export const DiscordEventsContext = createContext<DiscordEventsApi | null>(null);

export const DiscordEventsProvider = ({ children }: PropsWithChildren) => {
  const isAuthenticated = useAuth((c) => c.state === 'authenticated');
  const { platform } = usePlatform();
  const [speakers, setSpeakers] = useState<Set<string>>(new Set());

  const onStartSpeaking = ({ user_id }: { user_id: string }) => {
    setSpeakers((prev) => {
      if (prev.has(user_id)) return prev;
      const next = new Set(prev);
      next.add(user_id);
      return next;
    });
  };

  const onStopSpeaking = ({ user_id }: { user_id: string }) => {
    setSpeakers((prev) => {
      if (!prev.has(user_id)) return prev;
      const next = new Set(prev);
      next.delete(user_id);
      return next;
    });
  };

  useEffect(() => {
    if (platform.platform !== Platform.DiscordActivity) return;
    if (!isAuthenticated) return;
    const { discordSdk } = platform as DiscordActivityPlatformApi;
    const speakingSubOptions = { channel_id: discordSdk.channelId };

    discordSdk.subscribe('SPEAKING_START', onStartSpeaking, speakingSubOptions);
    discordSdk.subscribe('SPEAKING_STOP', onStopSpeaking, speakingSubOptions);
  }, [isAuthenticated, platform]);

  const api = useMemo<DiscordEventsApi>(() => {
    const isDiscordUserSpeaking = (discordUserId?: string | null) => !!discordUserId && speakers.has(discordUserId);
    return { isDiscordUserSpeaking };
  }, [speakers]);

  return <DiscordEventsContext.Provider value={api}>{children}</DiscordEventsContext.Provider>;
};

function useDiscordEvents() {
  const api = useContext(DiscordEventsContext);
  if (!api) {
    throw new Error('useDiscordEvents must be used within DiscordEventsProvider');
  }
  return api;
}

function useIsDiscordUserSpeaking(discordUserId?: string | null) {
  const { isDiscordUserSpeaking } = useDiscordEvents();
  return isDiscordUserSpeaking(discordUserId ?? undefined);
}

export function useIsUserSpeaking(userId?: string | null) {
  const discordUserId = useDiscordId(userId);
  return useIsDiscordUserSpeaking(discordUserId);
}
